import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ResponseCatalog, RequestParams } from 'Models'
import {
	FieldsRequest,
	PartnerRequestComment,
	PartnerRequestCreateModel,
	PartnerRequestModel,
	PartnerRequestCreateOrderAgreementsComment,
	PartnerRequestResume,
	PartnerRequestUpdateModel,
	PartnerRequestUpdateOrderAgreementsComment,
	TypeIncorporationForm,
} from 'src/pages/partnerRequests'
import { Callback } from 'Src/types/utilTypes'

export type PartnerRequestState = {
	reload: boolean
	partnerRequest: ResponseCatalog<PartnerRequestModel>
	editPartnerRequest: PartnerRequestModel | null
	requestParams: RequestParams
	isLoading: boolean
	fieldsRequest: FieldsRequest
	isEdit: boolean
	showNotificationSms: boolean
	typePartner: TypeIncorporationForm
}

export const partnerRequestInitialState: PartnerRequestState = {
	reload: false,
	partnerRequest: new ResponseCatalog<PartnerRequestModel>(),
	editPartnerRequest: null,
	requestParams: new RequestParams(),
	isLoading: false,
	fieldsRequest: {
		fields: [],
		checking_role: null,
		files: null,
	},
	isEdit: false,
	showNotificationSms: true,
	typePartner: null,
}

export namespace PartnerRequestNS {
	export type GetPartnerRequestList = PayloadAction<{
		params: RequestParams
		clear?: boolean
		callback?: Callback
	}>
	export type GetOne = PayloadAction<string>
	export type SetOne = PayloadAction<PartnerRequestModel>
	export type UpdateOne = PayloadAction<{
		id: string
		data: PartnerRequestUpdateModel
		callback?: Callback
	}>
	export type CreateOne = PayloadAction<PartnerRequestCreateModel>
	export type SetPartnerRequestList = PayloadAction<ResponseCatalog<PartnerRequestModel>>
	export type SetRequestParams = PayloadAction<RequestParams>
	export type SetIsLoading = PayloadAction<boolean>
	export type UploadFile = PayloadAction<{
		id: string
		data: Partial<PartnerRequestComment>
	}>
	export type SetStatus = PayloadAction<{
		id: string
		status: string
	}>
	export type Resume = PayloadAction<{
		id: string
		data: PartnerRequestResume
	}>
	export type setStatusAgreement = PayloadAction<{
		id: string
		status: string
		callback?: Callback
	}>
	export type UploadFileAgreement = PayloadAction<{
		id: string
		data: Partial<PartnerRequestComment>
	}>
	export type GetFieldsRequest = PayloadAction
	export type SetFieldsRequest = PayloadAction<FieldsRequest>
	export type AgentComment = PayloadAction<{
		id: string
		data: Partial<PartnerRequestComment>
		callback?: Callback
	}>
	export type TmppComment = PayloadAction<{
		id: string
		data: Partial<PartnerRequestComment>
	}>
	export type DeleteFileAgreement = PayloadAction<{
		id: string
		idFile: number
	}>
	export type UploadFileInfotecs = PayloadAction<{
		requestId: string
		infotecs_files_attributes: File[]
	}>
	export type DeleteFileInfotecs = PayloadAction<{
		id: string
		requestId: string
	}>
	export type SetStatusFileInfotecs = PayloadAction<{
		id: string
		status: string
		requestId: string
	}>
	export type SetIsEdit = PayloadAction<boolean>
	export type SetShowNotificationSms = PayloadAction<boolean>
	export type CreateOrderAgreementsComment = PayloadAction<{
		data: PartnerRequestCreateOrderAgreementsComment
	}>
	export type UpdateOrderAgreementsComment = PayloadAction<{
		data: PartnerRequestUpdateOrderAgreementsComment
	}>
	export type SetTypePartner = PayloadAction<TypeIncorporationForm>
}

export const partnerRequestSlice = createSlice({
	name: 'partnerRequest',
	initialState: partnerRequestInitialState,
	reducers: {
		getPartnerRequestList: (state, action: PartnerRequestNS.GetPartnerRequestList) => {},
		setPartnerRequestList: (state, action: PartnerRequestNS.SetPartnerRequestList) => {
			state.reload = state?.reload ?? false
			state.partnerRequest = action.payload
		},
		setRequestParams: (state, action: PartnerRequestNS.SetRequestParams) => {
			state.requestParams = action.payload
		},
		clearAll: (state) => {
			state.partnerRequest = new ResponseCatalog<PartnerRequestModel>()
			state.requestParams = new RequestParams()
		},
		setIsLoading: (state, action: PartnerRequestNS.SetIsLoading) => {
			state.isLoading = action.payload
		},
		getOne: (state, action: PartnerRequestNS.GetOne) => {},
		setOne: (state, action: PartnerRequestNS.SetOne) => {
			state.editPartnerRequest = action.payload
		},
		updateOne: (state, action: PartnerRequestNS.UpdateOne) => {},
		createOne: (state, action: PartnerRequestNS.CreateOne) => {},
		uploadFile: (state, action: PartnerRequestNS.UploadFile) => {},
		setStatus: (state, action: PartnerRequestNS.SetStatus) => {},
		agentComment: (state, action: PartnerRequestNS.AgentComment) => {},
		tmppComment: (state, action: PartnerRequestNS.TmppComment) => {},
		resume: (state, action: PartnerRequestNS.Resume) => {},
		setStatusAgreement: (state, action: PartnerRequestNS.setStatusAgreement) => {},
		uploadFileAgreement: (state, action: PartnerRequestNS.UploadFileAgreement) => {},
		getFieldsRequest: (state, action: PartnerRequestNS.GetFieldsRequest) => {},
		setFieldsRequest: (state, action: PartnerRequestNS.SetFieldsRequest) => {
			state.fieldsRequest = action.payload
		},
		setIsEdit: (state, action: PartnerRequestNS.SetIsEdit) => {
			state.isEdit = action.payload
		},
		deleteFileAgreement: (state, action: PartnerRequestNS.DeleteFileAgreement) => {},
		uploadFileInfotecs: (state, action: PartnerRequestNS.UploadFileInfotecs) => {},
		deleteFileInfotecs: (state, action: PartnerRequestNS.DeleteFileInfotecs) => {},
		setStatusFileInfotecs: (state, action: PartnerRequestNS.SetStatusFileInfotecs) => {},
		setShowNotificationSms: (state, action: PartnerRequestNS.SetShowNotificationSms) => {
			state.showNotificationSms = action.payload
		},
		createOrderAgreementsComment: (
			state,
			action: PartnerRequestNS.CreateOrderAgreementsComment
		) => {},
		updateOrderAgreementsComment: (
			state,
			action: PartnerRequestNS.UpdateOrderAgreementsComment
		) => {},
		setTypePartner: (state, action: PartnerRequestNS.SetTypePartner) => {
			state.typePartner = action.payload
		},
	},
})
